// File #1. src/Components/Admin/AdminPanel.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';

// Import sub-components
import Users from './Users';
import InitialView from './InitialView';
import Region from './Region';
import Categories from './Categories';
import MarkerIcons from './MarkerIcons';
import Settings from './Settings';

// Import Custom Components
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

// Import Image
import AdminBg from '../../assets/images/AdminBackground.png';

export default function AdminPanel({ onClose, getMapCurrentState }) {
  const [activeTab, setActiveTab] = useState('USERS');

  // --- STATE FOR DIRTY CHECKING ---
  const [isDirty, setIsDirty] = useState(false);
  const [showCloseAlert, setShowCloseAlert] = useState(false);

  // --- MINIMIZE STATE ---
  const [isMinimized, setIsMinimized] = useState(false);

  // --- DRAG AND DROP STATE ---
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDraggingState, setIsDraggingState] = useState(false);
  const isDraggingRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });

  const panelRef = useRef(null);

  // Fortified focus grab with a timeout to ensure paint completion
  useEffect(() => {
    const focusTimer = setTimeout(() => {
      if (panelRef.current) {
        panelRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(focusTimer);
  }, []);

  // --- DRAG LOGIC ---
  const handlePointerDown = (e) => {
    // Ignore drag if clicking inside a button (like the close X)
    if (e.target.closest('button')) return;

    isDraggingRef.current = true;
    setIsDraggingState(true);
    startPosRef.current = {
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    };
    // Prevent accidental text selection while dragging
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const handlePointerMove = (e) => {
      if (!isDraggingRef.current) return;

      let newX = e.clientX - startPosRef.current.x;
      let newY = e.clientY - startPosRef.current.y;

      // Prevent panel from being dragged completely off the top of the screen
      if (newY < -120) newY = -120;

      setOffset({
        x: newX,
        y: newY,
      });
    };

    const handlePointerUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setIsDraggingState(false);
      // Restore text selection
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  // --- HANDLERS ---
  const handleCloseAttempt = useCallback(() => {
    if (isDirty) {
      setShowCloseAlert(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  // --- ESCAPE KEY LOGIC ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore the Escape key entirely if the user is typing in a text field
      if (
        e.key === 'Escape' &&
        document.activeElement &&
        document.activeElement.tagName === 'INPUT'
      ) {
        return;
      }

      if (e.key === 'Escape' && !showCloseAlert && !isMinimized) {
        // Stop the event from triggering anything else if we catch it
        e.preventDefault();
        e.stopPropagation();
        handleCloseAttempt();
      }
    };

    // Use the capture phase so we intercept it before MUI eats it
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [showCloseAlert, isMinimized, handleCloseAttempt]);

  const handleConfirmClose = () => {
    setShowCloseAlert(false);
    onClose();
  };

  const handleTabChange = (newTab) => {
    setIsDirty(false);
    setActiveTab(newTab);
  };

  // --- RENDER CONTENT ---
  const renderContent = () => {
    const commonProps = { setIsDirty };

    switch (activeTab) {
      case 'USERS':
        return <Users {...commonProps} />;
      case 'INITIAL_VIEW':
        return <InitialView getMapCurrentState={getMapCurrentState} {...commonProps} />;
      case 'REGION':
        return <Region {...commonProps} />;
      case 'CATEGORIES':
        return <Categories {...commonProps} />;
      case 'MARKER_ICONS':
        return <MarkerIcons {...commonProps} />;
      case 'SETTINGS':
        return <Settings {...commonProps} />;
      default:
        return null;
    }
  };

  // --- MENU ITEMS ---
  const menuItems = [
    { id: 'USERS', label: 'Permitted Users' },
    { id: 'INITIAL_VIEW', label: 'Initial View' },
    { id: 'REGION', label: 'Region' },
    { id: 'CATEGORIES', label: 'Categories' },
    { id: 'MARKER_ICONS', label: 'Marker Icons' },
    { id: 'SETTINGS', label: 'Settings' },
  ];

  return (
    <>
      {/* RESTORE BUTTON (Shows only when minimized) */}
      {isMinimized && (
        <Box sx={{ position: 'fixed', bottom: 24, left: 24, zIndex: 100001 }}>
          <Button
            variant='contained'
            onClick={() => setIsMinimized(false)}
            sx={{
              backgroundColor: '#1976d2',
              color: '#fff',
              fontWeight: 'bold',
              borderRadius: '8px',
              padding: '10px 20px',
              boxShadow: 3,
              '&:hover': { backgroundColor: '#115293' },
            }}>
            RESTORE ADMIN PANEL
          </Button>
        </Box>
      )}

      {/* 1. Static Backdrop Overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'rgba(0,0,0,0.2)',
          zIndex: 100000,
          cursor: 'default',
          display: isMinimized ? 'none' : 'block',
        }}
      />

      {/* 2. Moveable Panel */}
      <Box
        sx={{
          position: 'fixed',
          top: '120px',
          left: '50%',
          transform: `translate(calc(-50% + ${offset.x}px), ${offset.y}px)`,
          zIndex: 100001,
          display: isMinimized ? 'none' : 'block',
        }}>
        <Paper
          ref={panelRef}
          tabIndex={-1}
          elevation={0}
          sx={{
            width: '80vw',
            height: '80vh',
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'white',
            border: '1px solid rgba(0,0,0,0.1)',
            boxShadow: '8px 0px 12px -1px rgba(0,0,0,0.5)',
            outline: 'none',
          }}>
          {/* HEADER (Drag Handle) */}
          <Box
            onPointerDown={handlePointerDown}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 0.5,
              minHeight: '40px',
              borderBottom: '1px solid black',
              backgroundImage: `linear-gradient(rgba(184, 231, 253, .85), rgba(184, 231, 253, .85)), url(${AdminBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              color: 'white',
              flexShrink: 0,
              cursor: isDraggingState ? 'grabbing' : 'grab',
            }}>
            <Typography
              variant='h6'
              fontWeight='bold'
              sx={{
                fontSize: '1.2rem',
                color: 'black',
                pointerEvents: 'none',
              }}>
              Admin Configuration
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleCloseAttempt}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label='close'
                size='small'
                sx={{ color: 'black', p: 0.5 }}>
                <CloseIcon fontSize='small' />
              </IconButton>
            </Box>
          </Box>

          {/* MAIN BODY */}
          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
            {/* LEFT SIDEBAR */}
            <Box
              sx={{
                width: '150px',
                bgcolor: '#707070',
                flexShrink: 0,
                py: 0,
              }}>
              <List component='nav' disablePadding>
                {menuItems.map((item) => (
                  <ListItemButton
                    key={item.id}
                    selected={activeTab === item.id}
                    onClick={() => handleTabChange(item.id)}
                    sx={{
                      color: 'white',
                      borderBottom: '1px solid white',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                      },
                      '&.Mui-selected': {
                        bgcolor: '#e3f2fd',
                        '& .MuiTypography-root': {
                          fontWeight: 600,
                          color: '#2c23e0',
                        },
                        '&:hover': {
                          bgcolor: '#bbdefb',
                        },
                      },
                    }}>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontSize: '0.9rem' }}>{item.label}</Typography>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            <Divider orientation='vertical' flexItem sx={{ borderColor: 'black' }} />

            {/* RIGHT CONTENT */}
            <Box sx={{ flexGrow: 1, p: 0, overflowY: 'auto', bgcolor: '#fff' }}>
              {renderContent()}
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* CLOSE CONFIRMATION DIALOG */}
      <ConfirmDialog
        open={showCloseAlert}
        onClose={() => setShowCloseAlert(false)}
        onConfirm={handleConfirmClose}
        message='Do you want to discard those changes?'
        severity='warning'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
        }}
      />
    </>
  );
}
